/**
 * O Refúgio: a conversa dos dois.
 *
 * É o `ChatMessage` de `lib/features/chat/domain/models/chat_message.dart` com
 * as mesmas chaves, e o corte do `ChatService.getMessagesStream`: ordenar por
 * hora e ficar com as últimas cinquenta. O corte é da tela, não do armazém —
 * o servidor manda a coleção inteira e quem decide quanto mostrar é aqui.
 */

import type { Documento } from '../types/dados';
import { gravarData, lerData } from '../nucleo/tempo';

/** Quantas mensagens a conversa mostra. O `sublist` do `ChatService`. */
export const QUANTAS_MENSAGENS = 50;

export interface Mensagem {
  id: string;
  /** O apelido de quem falou. É por ele que a tela sabe de que lado desenhar. */
  quem: string;
  /** O `senderUid` do Flutter. O site não tem `uid`, então grava o apelido. */
  quemUid: string;
  texto: string;
  quando: Date;
}

/** O `ChatMessage.fromMap`: o que falta vira vazio, e a data vira agora. */
export function mensagemDeMapa(id: string, dados: Record<string, unknown>): Mensagem {
  return {
    id,
    quem: typeof dados.sender === 'string' ? dados.sender : '',
    quemUid: typeof dados.senderUid === 'string' ? dados.senderUid : '',
    texto: typeof dados.text === 'string' ? dados.text : '',
    quando: lerData(dados.timestamp) ?? new Date(),
  };
}

/** O `ChatMessage.toMap`, com os nomes que o Flutter gravou. */
export function mensagemParaMapa(m: Omit<Mensagem, 'id'>): Record<string, unknown> {
  return {
    sender: m.quem,
    senderUid: m.quemUid,
    text: m.texto,
    timestamp: gravarData(m.quando),
  };
}

/**
 * A conversa em ordem, cortada nas últimas cinquenta.
 *
 * Uma mensagem sem hora legível não some: o `fromMap` de lá também a aceita e
 * a coloca em agora, e sumir com o que alguém escreveu seria pior que ordená-la
 * errado.
 */
export function conversa(
  docs: ReadonlyArray<Documento<Record<string, unknown>>>,
  quantas = QUANTAS_MENSAGENS,
): Mensagem[] {
  const todas = docs.map((d) => mensagemDeMapa(d.id, d.dados));
  todas.sort((a, b) => a.quando.getTime() - b.quando.getTime());
  return todas.length <= quantas ? todas : todas.slice(todas.length - quantas);
}

/** "Neste dia": mesmo dia e mês, de outros anos. O `getMessagesFromThisDay`. */
export function mensagensDesteDia(mensagens: Mensagem[], hoje = new Date()): Mensagem[] {
  return mensagens.filter(
    (m) =>
      m.quando.getMonth() === hoje.getMonth() &&
      m.quando.getDate() === hoje.getDate() &&
      m.quando.getFullYear() !== hoje.getFullYear(),
  );
}

/** Hoje mostra só a hora; outro dia mostra `d/MM, HH:mm`. O `ChatBubble`. */
export function ehHoje(quando: Date, hoje = new Date()): boolean {
  return (
    quando.getDate() === hoje.getDate() &&
    quando.getMonth() === hoje.getMonth() &&
    quando.getFullYear() === hoje.getFullYear()
  );
}
